import styled from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/*
 * version3's Tasks page (routes/tasks.tsx). Radii follow its scale, where
 * --radius is 0.875rem: rounded-md 12px, rounded-lg 14px, rounded-xl 18px,
 * rounded-2xl 22px.
 */
const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const BORDER = 'var(--hd-border)';
const BG = 'var(--hd-background)';
const CARD = 'var(--hd-card)';
const MUTED = 'var(--hd-muted)';
const MUTED_40 = 'color-mix(in oklab, var(--hd-muted) 40%, transparent)';
const SHADOW_SM = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';

export const StyledTasks = styled.div`
  ${brandTheme}
  min-height: 100%;
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: ${FG};
  background: ${BG};
  -webkit-font-smoothing: antialiased;

  .tasks-main {
    width: 100%;
    max-width: 1480px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 24px 16px;
    }
  }
`;

export const StyledPageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }

  h1 {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  .subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }
`;

export const StyledSearch = styled.div`
  position: relative;
  width: 100%;
  max-width: 288px;

  svg {
    position: absolute;
    top: 50%;
    left: 12px;
    width: 16px;
    height: 16px;
    color: ${MUTED_FG};
    transform: translateY(-50%);
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 40px;
    padding: 0 12px 0 36px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    color: ${FG};
    background: ${BG};
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: var(--hd-primary);
    }

    &::placeholder {
      color: ${MUTED_FG};
    }
  }
`;

export const StyledCategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StyledCategoryCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ $active }) => ($active ? `color-mix(in oklab, ${FG} 20%, transparent)` : BORDER)};
  border-radius: 18px;
  font-family: inherit;
  text-align: left;
  background: ${BG};
  box-shadow: ${({ $active }) => ($active ? SHADOW_SM : 'none')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    ${({ $active }) =>
      !$active &&
      `
        border-color: color-mix(in oklab, ${FG} 10%, transparent);
        background: ${MUTED_40};
      `}
  }

  &[aria-disabled='true'] {
    cursor: default;
  }

  .icon-wrap {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 14px;
    color: ${({ $active }) => ($active ? '#fff' : 'var(--hd-primary)')};
    background: ${({ $active }) => ($active ? 'var(--hd-gradient-houdiny)' : 'var(--hd-secondary)')};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .meta {
    flex: 1;
    min-width: 0;
  }

  .label-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: ${FG};
  }

  .soon {
    padding: 2px 8px;
    border: 1px solid ${BORDER};
    border-radius: 999px;
    font-size: 10px;
    font-weight: 500;
    white-space: nowrap;
    color: ${MUTED_FG};
    background: ${CARD};
  }

  .count {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .chevron {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${MUTED_FG};
  }
`;

export const StyledPanel = styled.div`
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${BG};
`;

export const StyledPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid ${BORDER};

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${FG};

    svg {
      width: 16px;
      height: 16px;
      color: ${MUTED_FG};
    }

    .meta {
      font-size: 14px;
      font-weight: 400;
    }
  }

  .meta {
    font-size: 12px;
    color: ${MUTED_FG};
  }
`;

export const StyledTaskList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const StyledTaskRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  transition: background 0.15s ease;

  &:not(:last-child) {
    border-bottom: 1px solid ${BORDER};
  }

  &:hover {
    background: ${MUTED_40};
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  .title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .title {
    font-size: 15px;
    font-weight: 600;
    color: ${FG};
  }

  .campaign-tag {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    color: ${MUTED_FG};
    background: ${MUTED};
  }

  .subtitle {
    margin-top: 4px;
    font-size: 14px;
    color: ${MUTED_FG};
  }

  .preview {
    margin-top: 12px;
    padding: 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-size: 14px;
    color: color-mix(in oklab, ${FG} 90%, transparent);
    background: ${CARD};
  }

  .meta-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
    font-size: 12px;
    color: ${MUTED_FG};

    .contact {
      font-weight: 500;
      color: color-mix(in oklab, ${FG} 80%, transparent);
    }

    .due {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
  }

  .actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
  }
`;

/* High is the brand colour; medium and low fade the muted foreground. */
export const StyledPriorityDot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 6px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${({ $priority }) => {
      if ($priority === 'high') return 'var(--hd-primary)';
      if ($priority === 'medium') return `color-mix(in oklab, ${MUTED_FG} 60%, transparent)`;
      return `color-mix(in oklab, ${MUTED_FG} 30%, transparent)`;
    }};
  }
`;

export const StyledAIApprovalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const StyledCampaignGroup = styled.section`
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  background: ${BG};
`;

export const StyledCampaignGroupHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border: none;
  font-family: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${MUTED_40};
  }

  .info {
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .name {
    font-size: 15px;
    font-weight: 600;
    color: ${FG};
  }

  .count-badge {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    color: ${MUTED_FG};
    background: ${MUTED};
  }

  .audience {
    margin-top: 2px;
    font-size: 12px;
    color: ${MUTED_FG};
  }

  .actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 12px;
  }

  .channel-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border: 1px solid ${BORDER};
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    color: ${FG};
    background: ${MUTED};

    svg {
      width: 12px;
      height: 12px;
    }
  }

  .chevron {
    width: 16px;
    height: 16px;
    color: ${MUTED_FG};
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
    transition: transform 0.15s ease;
  }
`;

export const StyledDraftList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${BORDER};
  background: ${CARD};
`;

/* A brand stripe down the left edge, whatever the channel. */
export const StyledDraftCard = styled.article`
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-left: 3px solid var(--hd-primary);
  border-radius: 18px;
  background: ${BG};
  box-shadow: ${SHADOW_SM};
`;

export const StyledDraftHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  font-size: 12px;
  color: ${FG};
  background: ${MUTED};

  .left {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-weight: 500;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .kind {
    color: color-mix(in oklab, ${FG} 80%, transparent);
  }

  .muted {
    color: ${MUTED_FG};
  }

  .drafted {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    color: ${MUTED_FG};

    svg {
      width: 12px;
      height: 12px;
      color: var(--hd-primary);
    }
  }
`;

export const StyledDraftPersonRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 0;

  .avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: #fff;
    background: var(--hd-gradient-houdiny);
  }

  .name {
    font-size: 14px;
    font-weight: 600;
    color: ${FG};
  }

  .role {
    font-size: 12px;
    color: ${MUTED_FG};
  }
`;

export const StyledDraftMessageBlock = styled.div`
  padding: 12px 16px 16px;

  .subject {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 13px;

    .label {
      color: ${MUTED_FG};
    }

    .value {
      font-weight: 500;
      color: ${FG};
    }

    .subject-input {
      flex: 1;
      min-width: 180px;
      height: 32px;
      padding: 0 10px;
      border: 1px solid ${BORDER};
      border-radius: 12px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      color: ${FG};
      background: ${BG};
      outline: none;

      &:focus {
        border-color: var(--hd-primary);
      }
    }
  }

  .body {
    padding: 12px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.625;
    white-space: pre-wrap;
    color: color-mix(in oklab, ${FG} 90%, transparent);
    background: ${MUTED};
    box-shadow: inset 0 0 0 1px ${BORDER};
  }

  /* The HTML variant carries its own breaks, so pre-wrap would double every
     blank line the <br>s already produce. */
  .body.is-html {
    max-height: 340px;
    overflow-y: auto;
    white-space: normal;
    overflow-wrap: anywhere;

    > :first-child {
      margin-top: 0;
    }

    > :last-child {
      margin-bottom: 0;
    }

    p {
      margin: 0 0 10px;
    }

    a {
      color: var(--hd-primary);
      text-decoration: underline;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    table {
      max-width: 100%;
    }

    blockquote {
      margin: 8px 0;
      padding-left: 10px;
      border-left: 2px solid ${BORDER};
      color: ${MUTED_FG};
    }
  }

  .editor-wrap {
    padding: 4px 8px;
    border-radius: 14px;
    background: ${MUTED};
    box-shadow: inset 0 0 0 1px ${BORDER};

    /* White writing surface with black text, as the TinyMCE iframe rendered. */
    .tt-editor {
      border: none;
      border-radius: 12px;
      background: #fff;
    }

    .tt-content-area .ProseMirror {
      color: #000;
    }
  }
`;

export const StyledDraftFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid ${BORDER};
  background: ${CARD};

  .hint {
    font-size: 11px;
    color: ${MUTED_FG};
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  border-radius: 14px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const StyledGhostButton = styled.button`
  ${buttonBase}
  padding: 0 12px;
  border: 1px solid ${BORDER};
  color: ${FG};
  background: ${BG};
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${MUTED};
  }
`;

export const StyledIconButton = styled.button`
  ${buttonBase}
  width: 32px;
  padding: 0;
  border: 1px solid ${BORDER};
  color: ${MUTED_FG};
  background: ${BG};
  transition: background 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: ${MUTED};
  }
`;

export const StyledPrimaryButton = styled.button`
  ${buttonBase}
  padding: 0 12px;
  border: none;
  color: ${BG};
  background: ${FG};
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const StyledActionButton = styled.button`
  ${buttonBase}
  padding: 0 12px;
  border: 1px solid ${({ $primary }) => ($primary ? 'transparent' : BORDER)};
  color: ${({ $primary }) => ($primary ? BG : FG)};
  background: ${({ $primary }) => ($primary ? FG : BG)};
  transition:
    opacity 0.15s ease,
    background 0.15s ease;

  &:hover:not(:disabled) {
    ${({ $primary }) => ($primary ? 'opacity: 0.9;' : `background: ${MUTED};`)}
  }
`;

export const StyledEmptyState = styled.div`
  padding: 64px 24px;
  border: 1px solid ${BORDER};
  border-radius: 22px;
  font-size: 14px;
  text-align: center;
  color: ${MUTED_FG};
  background: ${BG};

  /* Inside a panel that already draws the frame. */
  &.in-panel {
    border: none;
    border-radius: 0;
  }

  /* Inside an open campaign group. */
  &.inline {
    border: none;
    border-radius: 0;
    padding: 24px 8px;
  }
`;

export const StyledErrorState = styled(StyledEmptyState)`
  color: var(--hd-destructive);
`;

export const StyledLoadMore = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0 8px;
`;

export const StyledSkeletonCampaignHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: ${({ $collapsed }) => ($collapsed ? 'none' : `1px solid ${BORDER}`)};

  .info {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

export const StyledSkeletonDraftCard = styled.div`
  overflow: hidden;
  border: 1px solid ${BORDER};
  border-left: 3px solid color-mix(in oklab, var(--hd-primary) 30%, transparent);
  border-radius: 18px;
  background: ${BG};
  box-shadow: ${SHADOW_SM};

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    background: ${MUTED};
  }

  .person-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 16px 0;
  }

  .lines {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
  }

  .message {
    padding: 12px 16px 16px;
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-top: 1px solid ${BORDER};
    background: ${CARD};
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
